import React, { useState } from "react"
import {
  Autocomplete,
  Box,
  ClickAwayListener,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { nowUtc } from "@/utils/appNow"
import { OPTIMISED_FOR_LABELS } from "@/utils/routeHelper"
import CloseIcon from "@mui/icons-material/Close"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import InfoIcon from "@mui/icons-material/InfoOutlined"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import MyLocationIcon from "@mui/icons-material/MyLocation"
import SwipeRightAltIcon from "@mui/icons-material/SwipeRightAlt"
import logo from "@/assets/images/WetterWeltIcon.png"
import BlueSwitch from "@/components/ui/BlueSwitch"
import SegmentedPill from "@/components/ui/SegmentedPill"
import VesselAutocomplete from "@/components/ui/VesselAutocomplete"
import SpecPreviewCard from "@/features/voyagePlanner/wizard/SpecPreviewCard"
import { isDepartureOutdated } from "@/features/voyagePlanner/voyageSettingsFields"
import {
  PANEL_CARD_SX,
  CARD_TITLE_SX,
  ROW_LABEL_SX,
  ROW_DESCRIPTION_SX,
  PLAN_FIELD_SX,
  PLAN_DATEPICKER_SLOTPROPS,
  PLAN_DATEPICKER_FIELD_SX,
  PLAN_SPEED_FIELD_SX,
  PLAN_FIELD_CAPTION_SX,
  PLAN_UNIT_ADORNMENT_SX,
} from "@/theme/panelStyles"
import { PANEL_DIVIDER } from "@/theme/tokens"

const INFO_ICON_SX = { color: "#5b7f9c", fontSize: 16, cursor: "pointer" }

const CARD_SX = { ...PANEL_CARD_SX, mb: 1.25 }

function TooltipWithLogo({ children }) {
  return (
    <div style={{ display: "flex" }}>
      <img
        src={logo}
        alt="Meteotracker"
        style={{ height: "40px", marginRight: "8px" }}
      />
      <div>{children}</div>
    </div>
  )
}

function RestrictionRow({
  label,
  description,
  checked,
  disabled = false,
  onChange,
  ariaLabel,
  infoTitle,
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            ...ROW_LABEL_SX,
            color: disabled ? "text.disabled" : ROW_LABEL_SX.color,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {label}
          {infoTitle && (
            <Tooltip placement="top" title={infoTitle}>
              <InfoIcon sx={INFO_ICON_SX} />
            </Tooltip>
          )}
        </Typography>
        <Typography sx={ROW_DESCRIPTION_SX}>{description}</Typography>
      </Box>
      <BlueSwitch
        size="small"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        slotProps={{ input: { "aria-label": ariaLabel } }}
      />
    </Box>
  )
}

const ZONE_LIST_SX = {
  mt: 1,
  ml: 0.5,
  pl: 1.5,
  borderLeft: `2px solid ${PANEL_DIVIDER}`,
  display: "flex",
  flexDirection: "column",
  gap: 1,
}

// Grouped in the UI only; each zone stays its own flag in the core request.
function RiskZonesGroup({ title = "Avoid Risk Zones", zones, infoTitle }) {
  const [expanded, setExpanded] = useState(false)
  const selectable = zones.filter((z) => !z.disabled)
  const active = selectable.filter((z) => z.checked)
  const toggleExpanded = () => setExpanded((v) => !v)

  const setAll = (on) => {
    selectable.forEach((z) => {
      if (z.checked !== on) z.onChange(on)
    })
    setExpanded(on)
  }

  const label = (
    <>
      <Box component="span" onClick={toggleExpanded} sx={{ cursor: "pointer" }}>
        {title}
      </Box>
      <IconButton
        size="small"
        aria-label="toggle risk zone list"
        aria-expanded={expanded}
        onClick={toggleExpanded}
        sx={{ p: 0 }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: INFO_ICON_SX.color,
            transition: "transform 150ms",
            transform: expanded ? "rotate(180deg)" : "none",
          }}
        />
      </IconButton>
    </>
  )

  return (
    <ClickAwayListener
      mouseEvent={expanded ? "onMouseDown" : false}
      touchEvent={expanded ? "onTouchStart" : false}
      onClickAway={() => setExpanded(false)}
    >
      <Box>
        <RestrictionRow
          label={label}
          description={
            active.length
              ? active.map((z) => z.label).join(", ")
              : "No zones selected"
          }
          checked={active.length > 0}
          disabled={selectable.length === 0}
          onChange={setAll}
          ariaLabel="avoid-risk-zones-switch"
          infoTitle={infoTitle}
        />
        <Collapse in={expanded}>
          <Box sx={ZONE_LIST_SX}>
            {zones.map((z) => (
              <RestrictionRow
                key={z.key}
                label={z.label}
                description={z.description}
                checked={z.checked}
                disabled={z.disabled}
                onChange={z.onChange}
                ariaLabel={z.ariaLabel}
                infoTitle={z.infoTitle}
              />
            ))}
          </Box>
        </Collapse>
      </Box>
    </ClickAwayListener>
  )
}

// Step 1 · Plan — vessel/voyage selection, route endpoints, departure & speed,
// specification preview, routing restrictions. Purely presentational: all
// state and handlers are owned by VoyagePlannerPanel.
function PlanStep({
  // vessel + mode
  plannerVesselOptions,
  plannerSelectedVessel,
  onVesselChange,
  hasSelectedVessel,
  plannerRoutingMode,
  onRoutingModeChange,
  // voyage
  plannerSelectedVoyage,
  voyageChipMode = "existing",
  // route endpoints
  portOptions,
  getPortLabel,
  startAutoOpen,
  onStartAutoOpenChange,
  finishAutoOpen,
  onFinishAutoOpenChange,
  startInput,
  startError,
  onStartInputChange,
  onStartOptionSelect,
  finishInput,
  finishError,
  onFinishInputChange,
  onFinishOptionSelect,
  hasVesselLastPosition,
  hasVessel,
  onUseVesselPosition,
  canUseVoyageDestination,
  onUseVoyageDestination,
  // departure & speed
  calculatorDepartureTime,
  onDateChange,
  calculatorBasespeed,
  basespeedError,
  onBasespeedChange,
  // specifications preview
  plannerVesselSettings,
  currentDraft,
  onEditSpecifications,
  // routing restrictions
  applyRnwSafety,
  onApplyRnwSafetyChange,
  applyEca,
  onApplyEcaChange,
  avoidHra,
  onAvoidHraChange,
  avoidJwla,
  onAvoidJwlaChange,
  avoidCaa,
  onAvoidCaaChange,
  canSeeErklaerbaer,
  erklaerbaerEnabled,
  onErklaerbaerChange,
}) {
  // Departure must be a valid time that is not in the past — shared with the
  // compute-button gate via voyageSettingsFields so the two never drift.
  const departureMissing = !calculatorDepartureTime?.isValid?.()
  const departureOutdated = isDepartureOutdated(calculatorDepartureTime)
  const departureInvalid = departureMissing || departureOutdated
  const switchManualPathing = plannerRoutingMode === "manual"
  return (
    <>
      {/* Route card: optimisation mode · vessel · voyage · endpoints */}
      <Box sx={CARD_SX}>
      <Typography
        sx={{ ...CARD_TITLE_SX, mb: 1 }}
      >
        Vessel & Voyage
      </Typography>
      {/* Optimisation mode: Time | Fuel | Manual */}
      <Box
        data-tour="planner-mode"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 0.75,
        }}
      >
        <Tooltip
          placement="left"
          title={
            <TooltipWithLogo>
              Time and Fuel use automated pathfinding from WetterWelt — Time
              optimises the arrival, Fuel optimises fuel cost in USD (needs a
              bunker price and a consumption profile). Manual keeps routing in
              your hands so you can work with explicit waypoints by clicking
              on the map to add/remove points.
            </TooltipWithLogo>
          }
        >
          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#5b7f9c" }}>
            Optimisation
          </Typography>
        </Tooltip>
        <SegmentedPill
          options={Object.entries(OPTIMISED_FOR_LABELS).map(
            ([value, label]) => ({ value, label }),
          )}
          value={plannerRoutingMode}
          onChange={onRoutingModeChange}
        />
      </Box>
      {/* Vessel */}
      <Box data-tour="planner-vessel" sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.75 }}>
        <VesselAutocomplete
          options={plannerVesselOptions}
          value={plannerSelectedVessel}
          onChange={onVesselChange}
          error={!hasSelectedVessel}
          helperText={!hasSelectedVessel ? "Select a vessel to continue" : ""}
        />
      </Box>

      {/* Voyage chip — read-only: only one voyage can be active per vessel. */}
      {plannerSelectedVessel?.id != null && (
        <Box sx={{ mb: 1.25 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              maxWidth: "100%",
              px: 1,
              py: 0.4,
              borderRadius: "999px",
              bgcolor: "#eef3f7",
            }}
          >
            <SwipeRightAltIcon
              sx={{ fontSize: 16, color: "#004f7d", flexShrink: 0 }}
            />
            <Typography
              noWrap
              sx={{ fontSize: "12px", fontWeight: 600, color: "#004f7d", minWidth: 0 }}
            >
              {voyageChipMode === "new"
                ? "New voyage will be created on save"
                : voyageChipMode === "unavailable"
                  ? "Voyage could not be loaded"
                  : (plannerSelectedVoyage?.name ?? "Loading voyage…")}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Route endpoints */}
      <Box
        data-tour="planner-endpoints"
        sx={{
          display: "grid",
          gridTemplateColumns: "20px 1fr",
          columnGap: 1,
          rowGap: 0.75,
          alignItems: "center",
        }}
      >
        <LocationOnIcon sx={{ color: "#2e7d32", fontSize: 24 }} />
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 0.15,
              ml: 0.2,
            }}
          >
            <Typography
              sx={{ fontSize: "12px", color: "#57728d", fontWeight: 500 }}
            >
              Departure
            </Typography>
            <Tooltip
              title={
                hasVesselLastPosition
                  ? "Set departure to vessel's latest reported position"
                  : hasVessel
                    ? "Set departure to vessel's position along its plan route at departure time"
                    : "Select a vessel first"
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={!hasVessel}
                  onClick={onUseVesselPosition}
                  sx={{ color: "#2e7d32", p: 0.25 }}
                >
                  <MyLocationIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Autocomplete
            sx={{
              width: "100%",
              "& .MuiAutocomplete-input": { minWidth: "140px !important" },
            }}
            freeSolo
            autoSelect
            open={startAutoOpen}
            onOpen={() => onStartAutoOpenChange(true)}
            onClose={() => onStartAutoOpenChange(false)}
            options={startAutoOpen ? portOptions : []}
            getOptionLabel={getPortLabel}
            inputValue={startInput}
            onInputChange={onStartInputChange}
            onChange={onStartOptionSelect}
            disableClearable={false}
            clearIcon={<CloseIcon sx={{ fontSize: 14 }} />}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select port or enter lat,lon"
                variant="outlined"
                size="small"
                error={startError}
                fullWidth
                onFocus={(e) => e.target.select()}
                sx={PLAN_FIELD_SX}
              />
            )}
          />
        </Box>

        <LocationOnIcon sx={{ color: "#c62828", fontSize: 24 }} />
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 0.15,
              ml: 0.2,
            }}
          >
            <Typography
              sx={{ fontSize: "12px", color: "#57728d", fontWeight: 500 }}
            >
              Arrival
            </Typography>
            <Tooltip
              title={
                canUseVoyageDestination
                  ? "Set arrival to selected voyage's planned destination"
                  : "No voyage selected"
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={!canUseVoyageDestination}
                  onClick={onUseVoyageDestination}
                  sx={{ color: "#c62828", p: 0.25 }}
                >
                  <MyLocationIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Autocomplete
            sx={{
              width: "100%",
              "& .MuiAutocomplete-input": { minWidth: "140px !important" },
            }}
            freeSolo
            autoSelect
            open={finishAutoOpen}
            onOpen={() => onFinishAutoOpenChange(true)}
            onClose={() => onFinishAutoOpenChange(false)}
            options={finishAutoOpen ? portOptions : []}
            getOptionLabel={getPortLabel}
            inputValue={finishInput}
            onInputChange={onFinishInputChange}
            onChange={onFinishOptionSelect}
            disableClearable={false}
            clearIcon={<CloseIcon sx={{ fontSize: 14 }} />}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select port or enter lat,lon"
                variant="outlined"
                size="small"
                error={finishError}
                fullWidth
                onFocus={(e) => e.target.select()}
                sx={PLAN_FIELD_SX}
              />
            )}
          />
        </Box>
      </Box>
      </Box>

      {/* Departure time & base speed */}
      <Box sx={CARD_SX} data-tour="planner-departure">
        <Typography
          sx={{ ...CARD_TITLE_SX, mb: 1 }}
        >
          Departure & Speed
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <Box sx={{ flex: 1.4 }}>
            <Typography noWrap sx={PLAN_FIELD_CAPTION_SX}>
              Time (UTC)
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label={null}
                value={calculatorDepartureTime}
                onChange={onDateChange}
                minDateTime={nowUtc().startOf("minute")}
                ampm={false}
                format="YYYY-MM-DD HH:mm"
                slotProps={{
                  ...PLAN_DATEPICKER_SLOTPROPS,
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    error: departureInvalid,
                    helperText: departureOutdated
                      ? "Departure time is in the past."
                      : departureMissing
                        ? "Set a valid departure time"
                        : "",
                    sx: PLAN_DATEPICKER_FIELD_SX,
                    placeholder: "Select departure time",
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ flex: 0.8 }}>
            <Typography noWrap sx={{ ...PLAN_FIELD_CAPTION_SX, gap: 0.5 }}>
              Base Speed
              <Tooltip
                placement="top"
                title={
                  <TooltipWithLogo>
                    Vessel speed in [kn] under calm weather and normal
                    operating conditions.
                    <br />
                    Value must be a positive number.
                  </TooltipWithLogo>
                }
              >
                <InfoIcon sx={INFO_ICON_SX} />
              </Tooltip>
            </Typography>
            <TextField
              type="text"
              value={calculatorBasespeed || ""}
              onChange={onBasespeedChange}
              error={basespeedError}
              slotProps={{
                formHelperText: { sx: { m: 0, p: 0 } },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={PLAN_UNIT_ADORNMENT_SX}>kn</Typography>
                    </InputAdornment>
                  ),
                },
              }}
              margin="none"
              variant="outlined"
              fullWidth
              sx={PLAN_SPEED_FIELD_SX}
            />
          </Box>
        </Box>
      </Box>

      {/* Specifications preview */}
      <SpecPreviewCard
        plannerVesselSettings={plannerVesselSettings}
        currentDraft={currentDraft}
        onEdit={onEditSpecifications}
      />

      {/* Routing restrictions */}
      <Box sx={{ ...CARD_SX, mb: 0.5 }} data-tour="planner-restrictions">
        <Typography
          sx={{ ...CARD_TITLE_SX, mb: 1 }}
        >
          Routing Restrictions
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <RestrictionRow
            label="RNW Safety"
            description="Automatically route around heavy weather"
            checked={applyRnwSafety}
            disabled={switchManualPathing}
            onChange={onApplyRnwSafetyChange}
            ariaLabel="rnw-safety-switch"
            infoTitle={
              <TooltipWithLogo>
                Toggles all Route Network (RNW) safety logic: vessel & voyage
                restrictions, route leg limits and via area closures.
              </TooltipWithLogo>
            }
          />
          <RestrictionRow
            label={switchManualPathing ? "Check ECA" : "Avoid ECA"}
            description={
              switchManualPathing
                ? "ECA fuel share and costs, waypoints inside flagged"
                : "Route around Emission Control Areas"
            }
            checked={applyEca}
            onChange={onApplyEcaChange}
            ariaLabel="avoid-eca-switch"
          />
          <RiskZonesGroup
            title={switchManualPathing ? "Check Risk Zones" : "Avoid Risk Zones"}
            infoTitle={
              switchManualPathing ? (
                <TooltipWithLogo>
                  The manual route is not changed: waypoints and passages
                  inside the selected zones are flagged.
                </TooltipWithLogo>
              ) : (
                <TooltipWithLogo>
                  Zone layers the route keeps clear of where the detour is
                  reasonable; waypoints inside are flagged. Turning the switch
                  on enables all available zones, the list lets you pick
                  individually.
                </TooltipWithLogo>
              )
            }
            zones={[
              {
                key: "hra",
                label: "Piracy high risk areas",
                description: "UKMTO / ReCAAP high risk areas",
                checked: avoidHra,
                onChange: onAvoidHraChange,
                ariaLabel: "avoid-hra-switch",
                infoTitle: switchManualPathing ? null : (
                  <TooltipWithLogo>
                    Piracy and armed-robbery high risk areas (UKMTO,
                    ReCAAP/IMB) are routed around where the detour is
                    reasonable; waypoints inside are flagged.
                  </TooltipWithLogo>
                ),
              },
              {
                key: "jwla",
                label: "JWC war risk areas",
                description: "Listed war risk zones",
                checked: avoidJwla,
                onChange: onAvoidJwlaChange,
                ariaLabel: "avoid-jwla-switch",
                infoTitle: switchManualPathing ? null : (
                  <TooltipWithLogo>
                    Areas listed by the Joint War Committee (Red Sea, Gulf of
                    Aden, Persian Gulf, Black Sea, Gulf of Guinea, …). Ports
                    inside stay reachable, the passage is flagged. Some waters
                    are both piracy and war risk areas and carry both warnings.
                  </TooltipWithLogo>
                ),
              },
              {
                key: "caa",
                label: "Operational risk areas",
                description: "Ice, freak waves, gap winds, fog, munitions, fishing grounds",
                checked: avoidCaa,
                onChange: onAvoidCaaChange,
                ariaLabel: "avoid-caa-switch",
                infoTitle: switchManualPathing ? null : (
                  <TooltipWithLogo>
                    Curated waters with operational hazards no other layer
                    covers: iceberg and fog areas, freak-wave and gap-wind
                    regions, munitions dumping grounds, dense fishing grounds.
                    Each area has its own avoidance strength and season;
                    seasonal areas apply by departure month. Routes take a
                    reasonable detour, waypoints inside are flagged.
                  </TooltipWithLogo>
                ),
              },
            ]}
          />
        </Box>
      </Box>

      {/* Report output — separate from the routing restrictions */}
      {canSeeErklaerbaer && (
        <Box sx={{ ...CARD_SX, mb: 0.5 }}>
          <RestrictionRow
            label="Route Analysis Report"
            description="Generate a detailed report with the calculation"
            checked={erklaerbaerEnabled}
            disabled={switchManualPathing}
            onChange={onErklaerbaerChange}
            ariaLabel="route-analysis-report-switch"
            infoTitle={
              <TooltipWithLogo>
                Generates a debug visualization of the A* search (extra
                computation time). It opens in a new tab when the run
                finishes; if the browser blocks the popup, a notification
                with an "Open now" button appears.
              </TooltipWithLogo>
            }
          />
        </Box>
      )}
    </>
  )
}

export default React.memo(PlanStep)
